import { useState } from "react";
import type { InspirationArtwork } from "@/library/model/wire.js";
import { cx } from "@/platform/ui";

/**
 * An artwork, in an aspect-ratio box.
 *
 * `object-contain` rather than `object-cover`, unlike the feed thumbnails this
 * is otherwise modelled on: a feed thumbnail is a signpost and may be cropped,
 * a painting may not. Hokusai's wave is landscape and El Greco's Toledo is
 * portrait, and cropping either to fit a tidy rectangle is exactly the kind of
 * small vandalism this feature exists not to commit.
 *
 * The src is always same-origin (`/api/inspiration/artworks/:id/image`) — the
 * CSP is `img-src 'self' data:`, so a museum CDN URL would simply not load.
 */
export function ArtworkImage({
  artwork,
  className,
  eager = false,
  fit = "box",
}: {
  artwork: InspirationArtwork;
  className?: string;
  eager?: boolean;
  /**
   * `box` — a fixed aspect-ratio frame the work is fitted into. What a grid of
   * tiles needs: uniform cells, so the eye reads a wall rather than a jumble.
   *
   * `natural` — the work sets its own size, capped by `max-h`. For the modal,
   * where the art is the point: a fixed 4:3 box there letterboxes a wide canvas
   * (The Horse Fair is 599×306 — nearly half the modal was black bars).
   */
  fit?: "box" | "natural";
}) {
  const [failed, setFailed] = useState(false);

  // The boot backfill hasn't reached this artwork yet, or the file is gone. Say
  // so quietly rather than showing a broken-image glyph — it resolves itself
  // once the backfill runs.
  const notCached = (
    <div className="flex h-full min-h-24 w-full items-center justify-center px-4 text-center text-[10.5px] font-semibold text-faint">
      Image not cached yet
    </div>
  );

  if (fit === "natural") {
    return failed ? (
      <div className={cx("rounded-lg bg-[#08090b] py-10", className)}>{notCached}</div>
    ) : (
      <img
        src={artwork.imageUrl}
        alt={`${artwork.title} — ${artwork.artist}`}
        loading={eager ? "eager" : "lazy"}
        onError={() => setFailed(true)}
        className={cx("mx-auto block max-h-[60dvh] w-auto max-w-full rounded-lg", className)}
      />
    );
  }

  return (
    <div
      className={cx(
        "relative overflow-hidden rounded-lg bg-[#08090b]",
        className ?? "aspect-[4/3] w-full",
      )}
    >
      {failed ? (
        notCached
      ) : (
        <img
          src={artwork.imageUrl}
          alt={`${artwork.title} — ${artwork.artist}`}
          loading={eager ? "eager" : "lazy"}
          onError={() => setFailed(true)}
          className="absolute inset-0 h-full w-full object-contain"
        />
      )}
    </div>
  );
}
